import React, { memo } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Sparkles, Check } from "lucide-react";
import { useAddToChat } from "../../../../components/chat/chatContext";
import PredictButton from "../../../../components/dashboards/components/shared/PredictButton";
import XPicksButton from "../../../../components/dashboards/components/shared/XPicksButton";
import PredictionBox from "../../../../components/dashboards/components/shared/PredictionBox";

const statusStyles = (status = '') => {
  const live = ['Live', 'LIVE', 'ongoing'].includes(status);
  const finished = ['Finished', 'FT', 'final'].includes(status);
  if (live) return { label: 'LIVE', badge: 'bg-red-500 text-white' };
  if (finished) return { label: 'FINAL', badge: 'bg-white/20 text-white' };
  return { label: 'Scheduled', badge: 'bg-white/10 text-gray-300' };
};

const MMADesktopFightCard = memo(({
  fight,
  isSelected,
  isFrozen = false,
  onToggleSelection,
  prediction
}) => {
  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  const first = fight?.fighters?.first || fight?.fighters?.home || fight?.fighters?.red || fight?.fighters?.[0];
  const second = fight?.fighters?.second || fight?.fighters?.away || fight?.fighters?.blue || fight?.fighters?.[1];
  const status = fight?.status?.long || fight?.status || '';
  const { label, badge } = statusStyles(status);
  const fightDate = (fight?.date || '').slice(0, 10);

  const isLive = ['Live', 'LIVE', 'ongoing'].includes(status);
  const isFinished = ['Finished', 'FT', 'final'].includes(status);
  const isUpcoming = !isLive && !isFinished;

  // Handler to stage prediction and navigate to chat
  const handleAnalyzeInChat = async () => {
    if (!prediction) return;

    const message = `Analyze this MMA prediction:

${first?.name} vs ${second?.name}

Prediction: ${prediction.winner}${prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% confidence)` : ''}
Confidence Level: ${prediction.confidence}
${prediction.reason ? `Reasoning: ${prediction.reason}` : ''}

Can you provide a deeper analysis of this matchup?`;

    await handleAddToChat(message);
    navigate('/chat');
  };

  return (
    <div className={`relative rounded-sm p-5 transition-all duration-200 border ${
      isSelected ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-white/10 bg-white/[0.03] hover:border-white/40'
    }`}>
      <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-gray-500">
        <span>{fight?.league?.name || fight?.event || 'Event'}</span>
        <span className={`px-2 py-0.5 rounded-full ${badge}`}>{label}</span>
      </div>

      <div className="mt-4 space-y-3">
        {[first, second].map((fighter, idx) => (
          <div key={idx} className="flex items-center gap-3">
            {fighter?.logo && (
              <img src={fighter.logo} alt={fighter.name} className="w-10 h-10 object-cover rounded-full border border-white/10 bg-black/40" loading="lazy" />
            )}
            <div>
              <p className="text-sm font-mono text-white tracking-tight leading-tight">{fighter?.name || 'Fighter'}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-3 border-t border-white/10 flex flex-col items-center gap-1 text-white/70 text-xs">
        <div className="flex items-center gap-2">
          <Clock size={12} />
          <span>{fightDate || 'TBD'}</span>
        </div>
        {fight?.time && <span className="text-white/60">{fight.time} {fight?.timezone || 'UTC'}</span>}
      </div>

      {/* Prediction Result (if available) */}
      {prediction && (
        <PredictionBox
          prediction={prediction}
          onAnalyze={handleAnalyzeInChat}
          variant="desktop" />
      )}

      {/* Action Buttons */}
      <div className="mt-3 grid grid-cols-2 gap-2 items-stretch">
        {/* xPicks Button */}
        {(isUpcoming || isLive) && (
          <div className="col-span-1">
            <XPicksButton
              gameData={{
                homeTeam: first?.name,
                awayTeam: second?.name,
                date: fightDate,
                time: fight?.time || 'TBD',
                gameId: fight?.id
              }}
              sport="mma"
              label="XPICKS"
            />
          </div>
        )}

        {/* Prediction Button */}
        {(isUpcoming || isLive) && onToggleSelection && (
          <div className="col-span-1">
            <PredictButton
              label={isFrozen ? "COOLDOWN" : isSelected ? "SELECTED" : isLive ? "PREDICT LIVE" : "PREDICT"}
              icon={isFrozen ? Clock : isSelected ? Check : Sparkles}
              isSelected={isSelected}
              isLive={isLive}
              isFrozen={isFrozen}
              onClick={(e) => {
                e.stopPropagation();
                if (!isFrozen) {
                  onToggleSelection(fight);
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
});

MMADesktopFightCard.displayName = 'MMADesktopFightCard';

export default MMADesktopFightCard;
